import { faExclamationCircle, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Accordion, Badge, Button, Card, Dropdown, Form, FormControl, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import BaseDialog from "../../../components/dialogs/baseDialog/BaseDialog";
import BaseSelect from "../../../components/baseSelect/BaseSelect";
import APIErrorHelper from "../../../helpers/APIErrorHelper";
import ValidationHelper from "../../../helpers/ValidationHelper";
import CamplaCoreService from "../../../services/CamplaCoreService";
import PluginHelper from "../../../helpers/PluginHelper";

class AuxiliumImport extends React.Component {
  constructor(props) {
    super(props);
    this.fileReader = new FileReader();

    this.semesterOptions = [
      { value: "spring", label: "ModuleClasses.SemesterSpring" },
      { value: "fall", label: "ModuleClasses.SemesterFall" }
    ];
    const semesterMap = new Map();
    this.semesterOptions.forEach(s => semesterMap.set(s.value, s));

    this.validationDefinition = {
      name: {
        defaultValue: "",
        rule: ValidationHelper.isString
      },
      members: {
        defaultValue: [],
        rule: ValidationHelper.isMemberArray
      },
      moduleId: {
        defaultValue: "",
        rule: ValidationHelper.isID
      },
      isFall: {
        defaultValue: true,
        rule: ValidationHelper.isBoolean
      },
      year: {
        defaultValue: parseInt(new Date().getFullYear()),
        initValid: true,
        rule: ValidationHelper.isYear
      }
    };
    this.state = {
      show: false,
      isLoading: false,
      extendedModuleClass: null,
      modulesMap: new Map(),
      semesterMap: semesterMap,
      moduleOptions: [],
      importSuccessful: false,
      validation: ValidationHelper.getValidation(this.validationDefinition),
      dialogApiError: ""
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onHide = this.onHide.bind(this);
    this.onUpload = this.onUpload.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.translateOptions = this.translateOptions.bind(this);
    this.resolveModuleById = this.resolveModuleById.bind(this);
    this.extractData = this.extractData.bind(this);
    this.validateStudents = this.validateStudents.bind(this);
  }

  handleChange(event) {
    this.setState({
      validation: ValidationHelper.update(event, this.state.validation)
    });
  }

  translateOptions(t, options) {
    if (Array.isArray(options)) {
      return options.map(o => ({ value: o.value, label: t(o.label) }));
    } else {
      return { value: options.value, label: t(options.label) };
    }
  }

  onHide() {
    this.setState({
      extendedModuleClass: null,
      importSuccessful: false,
      isLoading: false,
      show: false
    });
  }

  async onSubmit() {
    if (this.state.validation._isValid) {
      const validatedData = ValidationHelper.getData(this.state.validation);
      const userId = CamplaCoreService.getAuthenticatedUser()._id;

      const setupClasses = await PluginHelper.calculateAndCreateClasses(validatedData, userId, () => {
        console.error("Creation of class failed");
        this.setState({
          dialogApiError: APIErrorHelper.getError(
              {status: 500},
              () => this.setState({ dialogApiError: "" }),
              {},
              this.props.t
          )
        });
      })
      if (setupClasses){
        this.props.onCreate({
          class: setupClasses.createdClasses,
          moduleClass: setupClasses.createdModuleClasses
        });
        this.setState({
          extendedModuleClass: null,
          importSuccessful: false,
          isLoading: false,
          show: false,
          validation: ValidationHelper.getValidation(this.validationDefinition)
        });
      }
    }
  }

  resolveModuleById(id) {
    const m = this.state.modulesMap.has(id)
      ? this.state.modulesMap.get(id)
      : this.state.modulesMap.values().next().value;
    return m ? { value: m._id, label: m.name } : null;
  }

  async fetchData() {
    try {
      const modules = await CamplaCoreService.getAllModules();
      const moduleOptions = modules.map(m => ({ value: m._id, label: m.name }));
      const modulesMap = new Map();
      modules.forEach(m => modulesMap.set(m._id, m));
      this.setState({ modulesMap: modulesMap, moduleOptions });
      this.handleChange({
        target: {
          name: "moduleId",
          value: modules[0]._id
        }
      });
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        )
      });
    }
  }

  onUpload(event) {
    this.fileReader.readAsBinaryString(event.target.files[0]);
  }

  async validateStudents(workbook){
    // Check for correct xlsx sheet
    if (
        typeof workbook["Sheets"] === "undefined" ||
        typeof workbook.Sheets["NotenDefinitiv"] === "undefined"
    ) {
      throw new Error("Invalid xlsx document given");
    }

    const metaData = XLSX.utils.sheet_to_json(
        workbook.Sheets["NotenDefinitiv"],
        {
          range: "A1:B5",
          header: "A"
        }
    );

    // Check if meta data is at the correct place
    if (metaData.length !== 5 || typeof metaData[1]["B"] === "undefined") {
      throw new Error("Invalid xlsx document given");
    }

    const moduleClass = /^[0-9]*-([0-9]*)([A-Z]*)\..*-.*-.*-[0-9]*([^.]*).*\/(.*)$/gm.exec(
        metaData[1].B
    );
    // Validate the extracted meta information
    if (!moduleClass || moduleClass.length !== 5) {
      throw new Error("Invalid xlsx document given");
    }
    moduleClass.forEach(mc => {
      if (mc.length === 0) {
        throw new Error("Invalid xlsx document given");
      }
    });

    const students = XLSX.utils.sheet_to_json(
        workbook.Sheets["NotenDefinitiv"],
        {
          range: 7,
          header: [
            "studentNumber",
            "salutation",
            "surname",
            "name",
            "status",
            "grade",
            "remarks",
            "mail",
            "year"
          ]
        }
    );
    if (!Array.isArray(students) || students.length === 0) {
      throw new Error("Invalid xlsx document given");
    }


    const studentProfiles = students
        .filter(
            rawSt =>
                typeof rawSt["mail"] !== "undefined" && rawSt["mail"].indexOf("@") > 0
        )
        .map(rawSt => ({
          email: rawSt["mail"],
          firstname: rawSt["name"],
          lastname: rawSt["surname"]
        }));

    if (students.length !== studentProfiles.length) {
      throw new Error("Invalid xlsx document given");
    }

    let s = -1;
    const buckets = [];
    const validatedStudents = [];
    let studentCount = 0;
    for (let i = 0; i < studentProfiles.length; i++) {
      if (i % 5 === 0) {
        s++;
        buckets[s] = [];
      }
      buckets[s].push(studentProfiles[i]);
      studentCount++;
    }
    buckets.forEach(profiles => profiles.forEach(p => validatedStudents.push(p)))
    return validatedStudents;
  }

  async extractData(workbook) {

    const students = await PluginHelper.validateStudentsEmails((await this.validateStudents(workbook)));

    const now = new Date();
    return {
      members: students,
      moduleId: null,
      className: "",
      isFall: now.getMonth() > 7 || now.getMonth() < 1,
      year: now.getFullYear()
    };
  }

  componentDidMount() {
    this.fetchData();
    this.fileReader.onload = async evt => {
      this.setState({ isLoading: true });
      try {
        const importedData = await this.extractData(
          XLSX.read(evt.target.result, { type: "binary" })
        );
        const validation = {
          name: {
            defaultValue: importedData.className,
            initValid: true,
            rule: ValidationHelper.isString
          },
          members: {
            defaultValue: importedData.members,
            initValid: true,
            rule: ValidationHelper.isMemberArray
          },
          moduleId: {
            defaultValue: importedData.moduleId ? importedData.moduleId : "",
            initValid: false,
            rule: ValidationHelper.isID
          },
          isFall: {
            defaultValue: importedData.isFall,
            initValid: true,
            rule: ValidationHelper.isBoolean
          },
          year: {
            defaultValue: importedData.year,
            initValid: true,
            rule: ValidationHelper.isYear
          }
        };
        this.setState({
          validation: ValidationHelper.getValidation(validation),
          importSuccessful: true
        });
        this.handleChange({
          target: {
            name: "year",
            value: importedData.year
          }
        });
      } catch (err) {
        const error = APIErrorHelper.getError(
          { status: 412 },
          () => this.setState({ dialogApiError: "" }),
          {},
          this.props.t
        );
        this.setState({ dialogApiError: error });
      }
      this.setState({ isLoading: false });
    };
  }

  render() {
    const { t } = this.props;
    // eslint-disable-next-line
    const upload =
      !this.state.importSuccessful && !this.state.isLoading ? (
        <Form>
          <p>
            <ol>
              <li>
                {t("Plugins.AuxiliumImport.Form.GuidelineTextStep1")}
                &nbsp;
                <a
                  href={window.plugins.auxilium.link}
                  className="item-link"
                  target="_blank" rel="noreferrer"
                >
                  {t("Plugins.AuxiliumImport.Form.AuxiliumLink")}
                </a>
              </li>
              <li>{t("Plugins.AuxiliumImport.Form.GuidelineTextStep2")}</li>
              <li>{t("Plugins.AuxiliumImport.Form.GuidelineTextStep3")}</li>
              <li>{t("Plugins.AuxiliumImport.Form.GuidelineTextStep4")}</li>
              <li>{t("Plugins.AuxiliumImport.Form.GuidelineTextStep5")}</li>
              <li>{t("Plugins.AuxiliumImport.Form.GuidelineTextStep6")}</li>
            </ol>
            <br />
            <b>{t("Plugins.AuxiliumImport.Form.Description")}</b> <br />
            <b>{t("Plugins.AuxiliumImport.Form.GuidelineTextStepFinal")}</b>
          </p>
          <Form.File id="custom-file" label="Custom file input" custom>
            <Form.File.Input
              onChange={this.onUpload}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <Form.File.Label
              data-browse={t("Plugins.AuxiliumImport.Form.UploadButton")}
            />
          </Form.File>
        </Form>
      ) : (
        ""
      );
    const loading = this.state.isLoading ? (
      <div>
        <FontAwesomeIcon icon={faCircleNotch} spin />{" "}
        {t("Plugins.AuxiliumImport.Loading")}
      </div>
    ) : (
      ""
    );
    const form = this.state.importSuccessful ? (
      <Accordion defaultActiveKey="0">
        <Card>
          <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
            <Accordion.Toggle
              as={Button}
              variant="link"
              className="acc-link"
              eventKey="0"
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className={
                  this.state.validation.name.isValid &&
                    this.state.validation.members.isValid
                    ? "display-none"
                    : "mr-2 color-red"
                }
              />
              <span>{t("Plugins.AuxiliumImport.Form.ToggleClass")}</span>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Form.Group controlId="formClassName">
                <Form.Label>
                  {t("Plugins.AuxiliumImport.Form.ClassName")}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder={t(
                    "Plugins.AuxiliumImport.Form.ClassNamePlaceholder"
                  )}
                  value={this.state.validation.name.value}
                  isInvalid={!this.state.validation.name.isValid}
                  onChange={this.handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.AuxiliumImport.Form.ValidationError.Name")}
                </Form.Control.Feedback>
              </Form.Group>
              <span>
                {t("Plugins.AuxiliumImport.Form.Students")}
                <Badge variant="secondary" className="ml-1">
                  {this.state.validation.members.value.length}
                </Badge>
              </span>
              <Table size="sm" className="students-table mt-2">
                <thead>
                  <tr>
                    <th>{t("Plugins.AuxiliumImport.Form.ColumnName")}</th>
                    <th>{t("Plugins.AuxiliumImport.Form.ColumnMail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.validation.members.value.map(m => (
                    <tr>
                      <td>{m.name}</td>
                      <td>{m.email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
            <Accordion.Toggle
              as={Button}
              variant="link"
              className="acc-link"
              eventKey="1"
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className={
                  this.state.validation.moduleId.isValid &&
                    this.state.validation.year.isValid &&
                    this.state.validation.isFall.isValid
                    ? "display-none"
                    : "mr-2 color-red"
                }
              />
              <span>{t("Plugins.AuxiliumImport.Form.ToggleModuleClass")}</span>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="1">
            <Card.Body>
              <Form.Group controlId="formBasicModules">
                <Form.Label>
                  {t("Plugins.AuxiliumImport.Form.Module")}
                </Form.Label>
                <BaseSelect
                  options={this.state.moduleOptions}
                  placeholder={t(
                    "Plugins.AuxiliumImport.Form.ModulePlaceholder"
                  )}
                  onChange={s =>
                    this.handleChange({
                      target: {
                        name: "moduleId",
                        value: s.value
                      }
                    })
                  }
                  value={this.resolveModuleById(
                    this.state.validation.moduleId.value
                  )}
                />
                <Form.Control
                  type="hidden"
                  isInvalid={!this.state.validation.moduleId.isValid}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.AuxiliumImport.Form.ValidationError.ModuleId")}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formBasicYear">
                <Form.Label>{t("Plugins.AuxiliumImport.Form.Year")}</Form.Label>
                <FormControl
                  type="number"
                  min={new Date().getFullYear() - 5}
                  max={new Date().getFullYear() + 5}
                  value={this.state.validation.year.value}
                  isInvalid={!this.state.validation.year.isValid}
                  onChange={event =>
                    this.handleChange({
                      target: {
                        name: "year",
                        value: parseInt(event.target.value)
                      }
                    })
                  }
                  onBlur={event => {
                    const val = parseInt(event.target.value);
                    const currentYear = new Date().getFullYear();
                    this.handleChange({
                      target: {
                        name: "year",
                        value:
                          val > currentYear - 5 && val < currentYear + 5
                            ? val
                            : currentYear
                      }
                    });
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.AuxiliumImport.Form.ValidationError.Year")}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formBasicSemester">
                <Form.Label>
                  {t("Plugins.AuxiliumImport.Form.Semester")}
                </Form.Label>
                <BaseSelect
                  isSearchable={false}
                  options={this.translateOptions(t, this.semesterOptions)}
                  onChange={selected => {
                    this.handleChange({
                      target: {
                        name: "isFall",
                        value: selected.value === "fall"
                      }
                    });
                  }}
                  placeholder={t(
                    "Plugins.AuxiliumImport.Form.SemesterPlaceholder"
                  )}
                  value={this.translateOptions(
                    t,
                    this.state.semesterMap.get(
                      this.state.validation.isFall.value ? "fall" : "spring"
                    )
                  )}
                />
                <Form.Control
                  type="hidden"
                  isInvalid={!this.state.validation.isFall.isValid}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.AuxiliumImport.Form.ValidationError.IsFall")}
                </Form.Control.Feedback>
              </Form.Group>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    ) : (
      ""
    );
    return (
      <>
        <Dropdown.Item onClick={() => this.setState({ show: true })}>
          {t("Plugins.AuxiliumImport.Button")}
        </Dropdown.Item>
        <BaseDialog
          show={this.state.show}
          onHide={this.onHide}
          dialogApiError={this.state.dialogApiError}
          title={t("Plugins.AuxiliumImport.Title")}
          leftButtonCallback={this.onHide}
          leftButtonText={t("DialogCancel")}
          leftButtonVariant="black"
          rightButtonCallback={this.onSubmit}
          rightButtonDisabled={!this.state.validation._isValid}
          rightButtonText={t("Plugins.AuxiliumImport.Form.Submit")}
          rightButtonVariant="green"
          body={(<>
            {upload}
            {loading}
            {form}
          </>)}
        />
      </>
    );
  }
}

export default withTranslation()(AuxiliumImport);
